// =============================================================================
// Supabase auth cookie SCOPE — single source of truth
// =============================================================================
//
// Every AMTIL app shares one Supabase project, so every app reads and writes
// the same `sb-<ref>-auth-token` cookie. The OAuth callbacks always wrote it
// with `Domain=.amtil.co` (so admin/app/connect share a session), while the
// @supabase/ssr defaults used by middleware, server components and the browser
// client carry NO domain — a host-only cookie on app.amtil.co. Both copies then
// coexist in the browser, neither writer can delete the other's copy, the
// browser sends both, and whichever copy wins is a coin flip: a months-old
// host-only session can shadow a login that succeeded one second earlier, or
// chunks from the two copies interleave and the merged value is rejected as
// corrupt (→ "Auth session missing" → /login loop). 2026-09-18, Jeremiah.
//
// Rule: an sb- auth cookie is written in exactly ONE scope per host, and every
// writer also deletes the other scope for the same name. Zero dependencies so
// the Edge middleware bundle and the browser can both import it.

export const AMTIL_AUTH_COOKIE_DOMAIN = '.amtil.co'

/** Base64 payload chunk count the callbacks may have written (0..N-1). */
export const AUTH_COOKIE_MAX_CHUNKS = 10

export type AuthCookieWrite = {
  name: string
  value: string
  options?: Record<string, unknown>
}

/**
 * Cookie domain for the given request hostname: `.amtil.co` on the apex or any
 * subdomain, undefined everywhere else (localhost, *.vercel.app previews — a
 * Domain attribute the browser cannot match is silently dropped, which would
 * make login impossible there).
 */
export function authCookieDomainFor(hostname: string | null | undefined): string | undefined {
  if (!hostname) return undefined
  const host = hostname.toLowerCase().split(':')[0]
  const apex = AMTIL_AUTH_COOKIE_DOMAIN.slice(1)
  return host === apex || host.endsWith(AMTIL_AUTH_COOKIE_DOMAIN) ? AMTIL_AUTH_COOKIE_DOMAIN : undefined
}

/**
 * Hostname of an incoming request, tolerant of partial request objects (unit
 * tests often pass a bare Request or a stub without `nextUrl`).
 */
export function requestHostname(request: {
  nextUrl?: { hostname?: string } | null
  headers?: { get(name: string): string | null } | null
  url?: string | null
}): string | undefined {
  const fromNext = request.nextUrl?.hostname
  if (fromNext) return fromNext
  const fromHeader = request.headers?.get?.('host')
  if (fromHeader) return fromHeader
  try {
    return request.url ? new URL(request.url).hostname : undefined
  } catch {
    return undefined
  }
}

export function isSupabaseAuthCookieName(name: string): boolean {
  return name.startsWith('sb-')
}

const HOST_ONLY_DELETE: Record<string, unknown> = { path: '/', maxAge: 0 }

/**
 * Expand a batch of cookie writes so every sb- cookie is written in the domain
 * scope AND deleted in the host-only scope (the leftover the ssr defaults used
 * to create). Non-auth cookies and non-amtil hosts (domain undefined) pass
 * through untouched. Deletes come first so a set for the same name wins.
 */
export function withAuthCookieScope(
  cookiesToSet: ReadonlyArray<AuthCookieWrite>,
  domain: string | undefined,
): AuthCookieWrite[] {
  if (!domain) return [...cookiesToSet]
  const out: AuthCookieWrite[] = []
  for (const write of cookiesToSet) {
    if (!isSupabaseAuthCookieName(write.name)) {
      out.push(write)
      continue
    }
    out.push({ name: write.name, value: '', options: { ...HOST_ONLY_DELETE } })
    // The shared domain is https-only; never downgrade the Secure cookie the
    // login callbacks wrote (same name/domain/path → this write replaces it).
    const secure = write.value === '' ? {} : { secure: true }
    out.push({ ...write, options: { ...(write.options ?? {}), ...secure, domain } })
  }
  return out
}

/**
 * Delete writes for an auth cookie and all of its chunks in the ONE scope a
 * framework cookie API can carry (name-keyed): the shared domain when there is
 * one, else host-only. Pair with `appendSetCookieHeaders(expireAuthCookieAllScopes(name, undefined))`
 * for the host-only twins when a domain is set.
 */
export function expireAuthCookieCookieApiScope(
  cookieName: string,
  domain: string | undefined,
  chunks: number = AUTH_COOKIE_MAX_CHUNKS,
): AuthCookieWrite[] {
  return expireAuthCookieAllScopes(cookieName, domain, chunks).filter((w) => !domain || w.options?.domain)
}

/**
 * Delete writes for an auth cookie and all of its chunks, in BOTH scopes.
 * Used before a login writes a fresh session so no stale copy survives.
 */
export function expireAuthCookieAllScopes(
  cookieName: string,
  domain: string | undefined,
  chunks: number = AUTH_COOKIE_MAX_CHUNKS,
): AuthCookieWrite[] {
  const names = [cookieName]
  for (let i = 0; i < chunks; i += 1) names.push(`${cookieName}.${i}`)
  const out: AuthCookieWrite[] = []
  for (const name of names) {
    out.push({ name, value: '', options: { ...HOST_ONLY_DELETE } })
    if (domain) out.push({ name, value: '', options: { ...HOST_ONLY_DELETE, domain } })
  }
  return out
}

/**
 * Split a batch of cookie writes into (a) the domain-scoped writes that should
 * go through the framework's cookie API and (b) the host-only deletes that must
 * be appended as raw Set-Cookie headers. Next's ResponseCookies is keyed by
 * cookie NAME, so a host-only delete and a domain write for the same name
 * cannot both live in it — the later call silently replaces the earlier one.
 */
export function splitAuthCookieWrites(
  cookiesToSet: ReadonlyArray<AuthCookieWrite>,
  domain: string | undefined,
): { sets: AuthCookieWrite[]; deferred: AuthCookieWrite[] } {
  const sets: AuthCookieWrite[] = []
  const deferred: AuthCookieWrite[] = []
  for (const write of withAuthCookieScope(cookiesToSet, domain)) {
    const isHostOnlyDelete =
      isSupabaseAuthCookieName(write.name) && write.value === '' && write.options?.maxAge === 0 && !write.options?.domain
    if (domain && isHostOnlyDelete) deferred.push(write)
    else sets.push(write)
  }
  return { sets, deferred }
}

/**
 * Names of sb- auth cookies that appear MORE THAN ONCE in a raw Cookie header.
 * The browser sends both scopes' copies; frameworks collapse duplicates by name
 * before app code sees them, so only the raw header reveals the dual-scope
 * state. Every such name gets its host-only copy purged.
 */
export function duplicateAuthCookieNames(rawCookieHeader: string | null | undefined): string[] {
  if (!rawCookieHeader) return []
  const seen = new Map<string, number>()
  for (const part of rawCookieHeader.split(';')) {
    const eq = part.indexOf('=')
    const name = (eq === -1 ? part : part.slice(0, eq)).trim()
    if (!name || !isSupabaseAuthCookieName(name)) continue
    seen.set(name, (seen.get(name) ?? 0) + 1)
  }
  return [...seen.entries()].filter(([, n]) => n > 1).map(([name]) => name)
}

export function hostOnlyDeletes(names: ReadonlyArray<string>): AuthCookieWrite[] {
  return names.map((name) => ({ name, value: '', options: { ...HOST_ONLY_DELETE } }))
}

/**
 * True only when the Auth API definitively rejected the session (missing,
 * invalid, expired-and-unrefreshable, revoked). Transient failures — timeouts
 * (no status), 5xx, 408, 429 rate limits — must NOT clear the shared cookie:
 * a Supabase blip would otherwise log the user out of every AMTIL subdomain.
 */
export function isDefiniteAuthRejection(error: { status?: number } | null | undefined): boolean {
  const status = error?.status
  if (status === undefined || status === null) return false
  return status >= 400 && status < 500 && status !== 408 && status !== 429
}

/**
 * Write a freshly minted session as the ONLY auth cookie in the browser:
 * expire every prior copy in the scope the cookie API can carry, write the
 * (chunked) value domain-scoped, then append the host-only deletes as raw
 * headers. Owns the ordering invariant (raw headers survive only if no
 * cookies.set() follows) in one place for every OAuth/password callback.
 * Returns the number of chunks written (1 = unchunked).
 */
export function writeAuthSessionCookies(
  response: ResponseLike,
  cookieName: string,
  sessionValue: string,
  options: Record<string, unknown>,
  domain: string | undefined,
  chunkSize = 3000,
): number {
  for (const { name, value, options: o } of expireAuthCookieCookieApiScope(cookieName, domain)) {
    response.cookies.set({ name, value, ...(o ?? {}) })
  }
  const writeOptions = { ...options, domain }
  let chunks = 1
  if (sessionValue.length <= chunkSize) {
    response.cookies.set({ name: cookieName, value: sessionValue, ...writeOptions })
  } else {
    chunks = 0
    for (let i = 0; i < sessionValue.length; i += chunkSize, chunks += 1) {
      response.cookies.set({ name: `${cookieName}.${chunks}`, value: sessionValue.slice(i, i + chunkSize), ...writeOptions })
    }
  }
  if (domain) appendSetCookieHeaders(response.headers, expireAuthCookieAllScopes(cookieName, undefined))
  return chunks
}

/**
 * The `cookies` option for @supabase/ssr createBrowserClient: reads
 * document.cookie, writes the shared scope with Secure and deletes the
 * host-only twin. One definition for every browser client.
 */
export function browserAuthCookieHandlers(domain: string | undefined): {
  getAll(): Array<{ name: string; value: string }>
  setAll(cookiesToSet: SetCookie[]): void
} {
  return {
    getAll() {
      if (typeof document === 'undefined') return []
      return document.cookie
        .split(';')
        .map((c) => c.trim())
        .filter(Boolean)
        .map((c) => {
          const eq = c.indexOf('=')
          const name = eq === -1 ? c : c.slice(0, eq)
          const raw = eq === -1 ? '' : c.slice(eq + 1)
          let value = raw
          try { value = decodeURIComponent(raw) } catch { /* keep raw */ }
          return { name, value }
        })
    },
    setAll(cookiesToSet) {
      if (typeof document === 'undefined') return
      for (const write of withAuthCookieScope(cookiesToSet, domain)) document.cookie = serializeAuthCookie(write)
    },
  }
}

/** Append writes as raw Set-Cookie headers. MUST run after every cookies.set(). */
export function appendSetCookieHeaders(headers: Headers, writes: ReadonlyArray<AuthCookieWrite>): void {
  for (const write of writes) headers.append('set-cookie', serializeAuthCookie(write))
}

type CookieBag = {
  getAll(): Array<{ name: string; value: string }>
  set(cookie: { name: string; value: string; [attribute: string]: unknown }): unknown
}
type ResponseLike = { headers: Headers; cookies: CookieBag }
type RequestLike = { cookies: { getAll(): Array<{ name: string; value: string }>; set(cookie: { name: string; value: string }): unknown } }
type SetCookie = { name: string; value: string; options?: Record<string, unknown> }

/**
 * Per-request middleware context. `working` is the NextResponse.next() the
 * Supabase client writes refreshed tokens into; `deferred` collects the
 * host-only deletes (and any other clears) that must reach the browser.
 */
export type AuthCookieScopeContext = {
  domain: string | undefined
  deferred: AuthCookieWrite[]
  /** sb- names the browser sent in both scopes (from the raw Cookie header). */
  dualScopeNames: string[]
  /** Winning values of dual-scope names, re-persisted domain-scoped once auth verified them. */
  rewrites: AuthCookieWrite[]
  /**
   * Set by the middleware ONLY after the Auth server (or a local JWT check)
   * accepted the session read from the collapsed cookies. Self-heal acts only
   * then: cookie order in the header follows first-creation, not last refresh,
   * so an unverified "winner" could be the stale copy.
   */
  authVerified: boolean
  working?: ResponseLike
}

const SELF_HEAL_MAX_AGE = 60 * 60 * 24 * 30

/**
 * @param parsedCookies the framework's already-collapsed view of the request
 *   cookies (last duplicate wins in Next) — i.e. the copy auth actually used.
 */
/**
 * Queue a both-scope delete of `name` for the response the middleware ends up
 * returning. With a shared domain the deletes go out as raw headers (name-keyed
 * cookie API cannot carry two scopes); without one, the host-only delete is set
 * on the working response so a later write of the same name still wins.
 */
export function queueAuthCookieClear(ctx: AuthCookieScopeContext, response: ResponseLike, name: string): void {
  const writes = expireAuthCookieAllScopes(name, ctx.domain, 0)
  if (ctx.domain) ctx.deferred.push(...writes)
  else writes.forEach((w) => response.cookies.set({ name: w.name, value: w.value, ...(w.options ?? {}) }))
}

/**
 * The `cookies` option for @supabase/ssr createServerClient inside middleware:
 * reads go through the sanitizer supplied by the app; writes land domain-scoped
 * on the request + working response, host-only twins are deferred to
 * finalizeAuthCookies. One implementation for every middleware.
 */
export function createScopedCookieHandlers(
  ctx: AuthCookieScopeContext,
  request: RequestLike,
  response: ResponseLike,
  sanitize: (cookies: Array<{ name: string; value: string }>) => { cookies: Array<{ name: string; value: string }> },
): { getAll(): Array<{ name: string; value: string }>; setAll(cookiesToSet: SetCookie[]): void } {
  return {
    getAll() {
      return sanitize(request.cookies.getAll()).cookies
    },
    setAll(cookiesToSet) {
      const { sets, deferred } = splitAuthCookieWrites(cookiesToSet, ctx.domain)
      sets.forEach(({ name, value, options }) => {
        request.cookies.set({ name, value, ...(options ?? {}) })
        response.cookies.set({ name, value, ...(options ?? {}) })
      })
      ctx.deferred.push(...deferred)
    },
  }
}

/**
 * Browser-side: delete every sb- cookie in both scopes (sign-out, decline,
 * circuit breaker). Replaces the hand-rolled NODE_ENV/isProduction variants.
 */
const LEGACY_AUTH_COOKIE_PREFIXES = ['supabase.auth.token', 'supabase-auth-token']

export function clearBrowserAuthCookies(): string[] {
  if (typeof document === 'undefined') return []
  const host = window.location.hostname
  const domain = authCookieDomainFor(host)
  const isAuthName = (n: string) => isSupabaseAuthCookieName(n) || LEGACY_AUTH_COOKIE_PREFIXES.some((p) => n.startsWith(p))
  const names = [...new Set(document.cookie.split(';').map((c) => c.split('=')[0].trim()).filter(isAuthName))]
  for (const name of names) {
    for (const w of expireAuthCookieAllScopes(name, domain, 0)) document.cookie = serializeAuthCookie(w)
    // A copy set with Domain=<exact host> (older CMS writer) is a third scope.
    document.cookie = serializeAuthCookie({ name, value: '', options: { ...HOST_ONLY_DELETE, domain: host } })
  }
  return names
}

export function createAuthCookieScope(
  hostname: string | null | undefined,
  rawCookieHeader?: string | null,
  parsedCookies: ReadonlyArray<{ name: string; value: string }> = [],
): AuthCookieScopeContext {
  const domain = authCookieDomainFor(hostname)
  const ctx: AuthCookieScopeContext = { domain, deferred: [], dualScopeNames: [], rewrites: [], authVerified: false }
  if (!domain) return ctx
  // Self-healing (applied in finalizeAuthCookies, only once `authVerified`): a
  // browser holding both scopes' copies gets the copy the framework kept — the
  // one auth just accepted — re-written domain-scoped, then the host-only twin
  // purged. Rewriting first means purging can never strand a stale domain copy
  // when the host-only one was the fresher (the legacy shape: login wrote the
  // domain copy months ago, ssr-default refreshes wrote host-only since).
  ctx.dualScopeNames = duplicateAuthCookieNames(rawCookieHeader)
  for (const name of ctx.dualScopeNames) {
    const winner = parsedCookies.find((c) => c.name === name)
    if (winner && winner.value) {
      ctx.rewrites.push({
        name,
        value: winner.value,
        options: { path: '/', maxAge: SELF_HEAL_MAX_AGE, sameSite: 'lax', secure: true, httpOnly: false, domain },
      })
    }
  }
  return ctx
}

/**
 * Run on WHATEVER response the middleware returns:
 *  1. NextResponse.redirect() is a fresh response, so the refreshed session the
 *     Supabase client wrote into `ctx.working` would otherwise be lost — copy
 *     its sb- cookies over (a rotated refresh token that never reaches the
 *     browser is a guaranteed logout on the next request).
 *  2. Append the deferred host-only deletes as raw headers, last, so no later
 *     cookies.set() re-serialisation can drop them.
 */
export function finalizeAuthCookies<R extends ResponseLike>(res: R, ctx: AuthCookieScopeContext): R {
  const deferred = [...ctx.deferred]
  // A delete queued for a name (auth failure, corrupt cookie) beats EVERYTHING
  // for that name: the working response's fresh write (refresh-then-reject in
  // one request), a self-heal rewrite, and the written-name filter below.
  // Only a DOMAIN-scoped queued delete marks a real clear (queueAuthCookieClear);
  // host-only deletes are the routine twin-cleanup that accompanies every write.
  const clearing = new Set(deferred.filter((w) => w.value === '' && w.options?.domain).map((w) => w.name))
  // Every other cookie the middleware set on its working response is
  // intentional (refreshed session, role cache) — carry all of them.
  if (ctx.working && ctx.working !== res) {
    for (const cookie of ctx.working.cookies.getAll()) {
      if (!clearing.has(cookie.name)) res.cookies.set(cookie)
    }
  }
  if (ctx.authVerified) {
    const alreadyWritten = new Set(res.cookies.getAll().filter((c) => c.value !== '').map((c) => c.name))
    for (const w of ctx.rewrites) {
      if (!clearing.has(w.name) && !alreadyWritten.has(w.name)) {
        res.cookies.set({ name: w.name, value: w.value, ...(w.options ?? {}) })
      }
    }
    deferred.push(...hostOnlyDeletes(ctx.dualScopeNames.filter((n) => !clearing.has(n))))
  }
  // Ensure nothing non-empty for a clearing name survives in the cookie map.
  for (const cookie of res.cookies.getAll()) {
    if (clearing.has(cookie.name) && cookie.value !== '') res.cookies.set({ name: cookie.name, value: '', path: '/', maxAge: 0 })
  }
  // Dedupe (sanitizer + rejection can queue the same delete twice), then emit.
  const seen = new Set<string>()
  appendSetCookieHeaders(
    res.headers,
    deferred.filter((w) => {
      const key = `${w.name}|${String(w.options?.domain ?? '')}|${w.value}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    }),
  )
  return res
}

/**
 * Serialize one write to a `document.cookie` assignment string. Mirrors the
 * attribute set @supabase/ssr emits; `maxAge: 0` is honoured as a delete.
 */
export function serializeAuthCookie(write: AuthCookieWrite): string {
  const o = write.options ?? {}
  const parts = [`${write.name}=${encodeURIComponent(write.value)}`]
  parts.push(`Path=${typeof o.path === 'string' && o.path ? o.path : '/'}`)
  if (typeof o.domain === 'string' && o.domain) parts.push(`Domain=${o.domain}`)
  if (typeof o.maxAge === 'number') parts.push(`Max-Age=${Math.max(0, Math.floor(o.maxAge))}`)
  if (o.expires instanceof Date) parts.push(`Expires=${o.expires.toUTCString()}`)
  if (typeof o.sameSite === 'string' && o.sameSite) {
    const s = o.sameSite.toLowerCase()
    parts.push(`SameSite=${s.charAt(0).toUpperCase()}${s.slice(1)}`)
  } else if (o.sameSite === true) {
    parts.push('SameSite=Strict')
  }
  if (o.secure) parts.push('Secure')
  return parts.join('; ')
}
